import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChildren,
  QueryList,
  ElementRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { OneWebComponentsAngularModule } from '@one/angular';
import { QnSbnService } from '../../../services/qn-sbn-service.service';
import { Subject, Subscription } from 'rxjs';
import { RichTextDocumentComponent } from '../../richTextDocument/rich-text-document/rich-text-document.component';
import { AddOwnTemplateDialogComponent } from '../../add-own-template-dialog/add-own-template-dialog.component';
import { snackbarService } from '@one/web-components';
@Component({
  selector: 'dl-message-attachment-template',
  standalone: true,
  imports: [CommonModule, FormsModule, OneWebComponentsAngularModule, AddOwnTemplateDialogComponent],
  templateUrl: './message-attachment-template.component.html',
  styleUrls: ['./message-attachment-template.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MessageAttachmentTemplateComponent implements OnInit {
  @Input() public isNewFromTemplate: boolean;
  @Input() public tableData: any[] = [];
  public dataSource: any[] = [];
  @Input() public displayedColumns = [];
  @ViewChildren('menu') menus: QueryList<any>;

  private subscription: Subscription;
  @Input() addRowData: any;
  @Output() public isMessageRowSelected = new EventEmitter<{ messageSelected: any[] }>();
  @Output() closeDialog = new EventEmitter<void>();
  public messageDta = {};
  visible: boolean = false;
  deleteButtonVisible = false;
  templateToDelete: any = null;
  selectedRow: any = null;
  public isInitialized: boolean = false;
  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private qnsbnservice: QnSbnService
  ) { }

  ngOnInit(): void {
    // Ensure selection is cleared on initialization
    this.selection.selected = null;
    this.selectedRow = null;
    this.isInitialized = false;

    this.dataSource = this.dataSource || [];
    if (this.tableData && this.tableData.length > 0) {
      this.dataSource = [...this.tableData];
    }

    // Set initialized flag after a microtask to ensure clean state
    Promise.resolve().then(() => {
      this.isInitialized = true;
      this.changeDetectorRef.detectChanges();
    });

    this.subscription = this.qnsbnservice.template$.subscribe(template => {
      if (!template?.data || !Array.isArray(template.data)) return;

      template.data.forEach(updatedTemplate => {
        const normalizedTemplate = {
          templateId: updatedTemplate.templateId || updatedTemplate.templateDetails?.templateId,
          templateSubject: updatedTemplate.templateSubject || updatedTemplate.templateDetails?.templateSubject,
          templateBody: updatedTemplate.templateBody || updatedTemplate.templateDetails?.templateBody,
        };
        if (normalizedTemplate.templateSubject && normalizedTemplate.templateBody) {
          const index = this.dataSource.findIndex(t => t.templateId === normalizedTemplate.templateId);

          if (index >= 0) {
            this.dataSource[index] = normalizedTemplate;
          } else {
            this.dataSource = [...this.dataSource, normalizedTemplate];
          }
        }
      });

      this.dataSource = [...this.dataSource];

      this.changeDetectorRef.detectChanges();
    });

    this.addRowData?.subscribe((data: any) => {
      const validTemplates = (data.templateData || [])
        .map(t => ({
          templateId: t.templateId,
          templateSubject: t.templateSubject || t.templateDetails?.templateSubject,
          templateBody: t.templateBody || t.templateDetails?.templateBody,
        }))
        .filter(t => t.templateSubject && t.templateBody);

      validTemplates.forEach(t => {
        const exists = this.dataSource.some(item => item.templateId === t.templateId);
        if (!exists) {
          this.dataSource = [...this.dataSource, t];
        }
      });
      this.changeDetectorRef.detectChanges();
    });
  }


  ngOnChanges(changes: SimpleChanges) {
    if (changes['tableData'] && changes['tableData'].currentValue) {
      const incomingTableData = changes['tableData'].currentValue;
      if (!this.dataSource.length || incomingTableData.length !== this.dataSource.length) {
        this.isInitialized = false;
        this.dataSource = [...incomingTableData];
        // Clear selection when tableData changes (e.g., when dialog reopens)
        this.clearSelection();
        this.changeDetectorRef.markForCheck();

        // Re-enable after next tick
        Promise.resolve().then(() => {
          this.isInitialized = true;
          this.changeDetectorRef.detectChanges();
        });
      }
    }
  }


  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  trackByTemplateId(index: number, row: any): string {
    return row.templateId;
  }

  public deleteMessageFromTemplate(elm: any, index: number): void {
    this.templateToDelete = elm;
    this.deleteButtonVisible = true;
  }

  public confirmDelete(): void {
    if (!this.templateToDelete) return;

    this.qnsbnservice.deleteMessageTemplate(this.templateToDelete.templateId).subscribe({
      next: (response: any) => {
        if (response?.errorMessage) {
          this.snackBarMessage('alarm', response?.errorMessage);
        } else {
          this.dataSource = this.dataSource.filter(item => item.templateId !== this.templateToDelete.templateId);
          this.tableData = this.tableData.filter(item => item.templateId !== this.templateToDelete.templateId);
          this.changeDetectorRef.detectChanges();
          this.snackBarMessage('positive', 'Message template successfully deleted from your templates.');
        }
        this.deleteButtonVisible = false;
        this.templateToDelete = null;
        this.changeDetectorRef.detectChanges();
      },
      error: (err) => {
        this.qnsbnservice.httpErrorHandler(err, 'Could not delete message template.');
        this.deleteButtonVisible = false;
        this.templateToDelete = null;
      }
    });
  }

  public closeDeleteModal(): void {
    this.deleteButtonVisible = false;
    this.templateToDelete = null;
  }

  public selection = {
    selected: null as any,
    select: (row: any) => {
      this.selection.selected = row;
    }
  };

  public clearSelection(): void {
    this.selection.selected = null;
    this.selectedRow = null;
    this.changeDetectorRef.detectChanges();
  }

  public onRadioClick(row: any, event: Event): void {
    // Don't stop propagation to allow the change event to fire

    // If clicking on already selected row and not disabled, do nothing
    if (this.selection.selected?.templateId === row.templateId) {
      return;
    }

    this.selectRadio(row);
  }

  public onRadioChange(row: any, event: Event): void {
    // Handle change event when clicking on label/text area
    if (this.selection.selected?.templateId === row.templateId) {
      return;
    }

    this.selectRadio(row);
  }

  private selectRadio(row: any): void {
    // Clear previous selection and select new row
    this.selection.selected = row;
    this.selectedRow = row;
    this.updateSelectRowStatus();
    this.changeDetectorRef.detectChanges();
  }

  public updateSelectRowStatus(): void {
    this.isMessageRowSelected.emit({ messageSelected: this.selection.selected });
  }


  public EditMessageTemplate(i: number): void {
    this.messageDta = {
      tableData: this.dataSource[i],
      columnData: [],
      rowIndex: i,
      allTemplates: this.dataSource,
      messageType: 'Email'
    };
    this.visible = true;
  }

  public closeMenu(): void {
    // Close all menu instances
    if (this.menus) {
      this.menus.forEach(menu => {
        if (menu && typeof menu.close === 'function') {
          menu.close();
        } else if (menu && menu.nativeElement) {
          // For owc-menu web component
          menu.nativeElement.open = false;
        }
      });
    }
    this.changeDetectorRef.detectChanges();
  }

  snackBarMessage(status: any, message: string) {
    snackbarService.show({
      message: message,
      snackbarProps: {
        status: status,
        direction: 'horizontal',
        showClose: true,
        elevated: false,
        duration: 5000,
        verticalPlacement: 'top',
        horizontalPlacement: 'right',
      }
    });
  }
}
